import React, { useRef, useEffect, useState } from "react";
import Highcharts from "highcharts";
import "../index.css";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import { navigateWithFai } from "../utils/Utils";
import { useTranslation } from "react-i18next";

const DARK_PLOT_BAND_COLORS = [
  "rgba(220, 55, 45, 0.28)",
  "rgba(200, 150, 0, 0.28)",
  "rgba(40, 160, 60, 0.28)",
  "rgba(55, 95, 195, 0.28)",
];
const DARK_PLOT_LABEL_COLORS = ["#dd8888", "#ccaa44", "#77cc77", "#7799ee"];

const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;
  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const LifestyleGraphV2Skeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
        position: "relative",
      }}
    >
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ ...shimmerBaseStyle, width: 32, height: 32, borderRadius: 999 }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ ...shimmerBaseStyle, height: 18, width: "55%", borderRadius: 8 }} />
            <div style={{ ...shimmerBaseStyle, height: 14, width: "40%", borderRadius: 8 }} />
          </div>
          <div style={{ ...shimmerBaseStyle, width: 20, height: 20, borderRadius: 999 }} />
        </div>
      </div>
      <div style={{ marginTop: 12, padding: "0 16px 16px 16px" }}>
        <div style={{ ...shimmerBaseStyle, height: 180, borderRadius: 16 }} />
      </div>
      <div
        style={{
          padding: "0 16px 16px 16px",
          display: "flex",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <div style={{ ...shimmerBaseStyle, height: 14, width: 80, borderRadius: 8 }} />
        <div style={{ ...shimmerBaseStyle, height: 14, width: 80, borderRadius: 8 }} />
      </div>
    </div>
  );
};

const LifestyleGraphV2 = () => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);

  const { theme } = getUrlParams();
  const isDark =
    theme === "dark" || document.documentElement.classList.contains("dark");

  useEffect(() => {
    const { encryptedId } = getUrlParams();
    if (!encryptedId) return;

    const compareWithUserId =
      new URL(window.location.href).searchParams.get("compareWithUserId") || "";

    lifestyleApi
      .graphV2Data(encryptedId, compareWithUserId)
      .then((response) => {
        if (response?.data?.code === 200) {
          setChartData(response.data.data);
        } else {
          console.error("Invalid response:", response);
        }
      })
      .catch((err) => console.error("Error loading graph v2 data:", err));
  }, []);

  const makePlotBands = (data) => {
    if (!Array.isArray(data.plots)) return [];
    return data.plots.map((p, i) => {
      const rawColor = p.label?.style?.color || "#606060";
      const safeColor = rawColor.replace(/^#+/, "#");
      const labelText = p.label?.text || null;
      const translatedText = labelText
        ? t(`lifestyleGraphV2.zones.${labelText}`, { defaultValue: labelText })
        : null;
      return {
        from: p.from,
        to: p.to,
        color: isDark
          ? DARK_PLOT_BAND_COLORS[i] || "rgba(255,255,255,0.06)"
          : p.color || "rgba(0,0,0,0.03)",
        label: {
          text: translatedText,
          align: "left",
          x: 8,
          verticalAlign: "middle",
          y: 0,
          style: {
            color: isDark
              ? DARK_PLOT_LABEL_COLORS[i] || "#aaaaaa"
              : safeColor,
            fontSize: "12px",
            fontWeight: "400",
            fontFamily: "Open Sans, sans-serif",
          },
        },
      };
    });
  };

  useEffect(() => {
    if (!chartData || !containerRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    chartRef.current = Highcharts.chart(containerRef.current, {
      chart: {
        type: "spline",
        backgroundColor: "transparent",
        marginTop: 10,
        spacingBottom: 0,
        height: 180,
        style: { fontFamily: "'Open Sans', sans-serif" },
      },
      title: { text: null },
      xAxis: {
        categories: chartData.xAxis,
        lineColor: "var(--border-subtle)",
        lineWidth: 1,
        labels: {
          overflow: "justify",
          style: {
            fontSize: "9px",
            color: isDark ? "#aaaaaa" : "#4a4a4a",
            fontFamily: "Open Sans, sans-serif",
          },
        },
      },
      yAxis: {
        title: { text: null },
        plotBands: makePlotBands(chartData),
        labels: { enabled: false },
        gridLineWidth: 0,
        min: 0,
        minRange: 675,
      },
      series: [
        {
          data: chartData.yAxis,
          name: chartData.name1,
          showInLegend: false,
          zoneAxis: "y",
          zones: chartData.zones,
          enableMouseTracking: false,
        },
        {
          data: chartData.yAxis2,
          name: chartData.name2,
          showInLegend: false,
          zoneAxis: "y",
          zones: chartData.zones2,
          enableMouseTracking: false,
        },
      ],
      plotOptions: {
        spline: {
          lineWidth: 4,
          states: { hover: { lineWidth: 4 } },
          marker: { enabled: false },
        },
        series: { animation: false },
      },
      tooltip: { enabled: false },
      legend: { enabled: false },
      credits: { enabled: false },
      accessibility: { enabled: false },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [chartData, isDark]);

  const showLegend = chartData?.legendColor1 && chartData?.legendColor2;

  if (!chartData) return <LifestyleGraphV2Skeleton />;

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
        paddingBottom: 16,
      }}
    >
      <SectionHeader
        icon={chartData.image}
        title={chartData.text}
        subtitle={chartData.joinedSince}
        showInfo={!!chartData.showInfo}
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        onInfoClick={() => navigateWithFai(chartData.text, chartData.infoUrl)}
      />

      <div ref={containerRef} style={{ width: "100%", height: 180 }} />

      {showLegend && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 20,
            paddingInline: 16,
            marginTop: 8,
          }}
        >
          {[
            { color: chartData.legendColor1, name: chartData.name1 },
            { color: chartData.legendColor2, name: chartData.name2 },
          ].map(({ color, name }) => (
            <div key={name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  backgroundColor: color,
                  border: "1px solid #ccc",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  color: "var(--text-primary)",
                  fontFamily: "Open Sans, sans-serif",
                }}
              >
                {name}
              </span>
            </div>
          ))}
        </div>
      )}

      {chartData.healthtext && (
        <p
          style={{
            fontSize: 9,
            fontWeight: 600,
            color: "var(--text-secondary)",
            fontFamily: "Open Sans, sans-serif",
            margin: "8px 16px 0 16px",
            textAlign: "left",
          }}
        >
          {chartData.healthtext}
        </p>
      )}
    </div>
  );
};

export default LifestyleGraphV2;
